import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaFileSignature, FaCheck, FaSpinner } from "react-icons/fa";

const PDFPreviewModal = ({ pdfBlob, onClose, onConfirm, isSubmitting }) => {
  const [pdfUrl, setPdfUrl] = useState("");
  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  useEffect(() => {
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [pdfBlob]);

  useEffect(() => {
    if (!modalRoot) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [modalRoot]);

  const handleConfirm = () => {
    // Automatically add current date
    const currentDate = new Date().toISOString().split('T')[0];
    
    onConfirm({
      signatureDate: currentDate
    });
  };

  if (!modalRoot) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4">
      <div className="bg-white dark:bg-dark-800 md:rounded-2xl rounded-none shadow-2xl w-full md:max-w-5xl h-full md:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-dark-700">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white">
              PDF Preview
            </h2>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">
              Review your checklist before submission
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-50"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50 dark:bg-dark-900/50">
          {/* PDF Preview Container */}
          <div className="bg-white dark:bg-dark-900 rounded-2xl p-3 md:p-4 border border-gray-100 dark:border-dark-800 shadow-sm">
            <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
              <FaFileSignature className="mr-2 text-primary-500 w-4 h-4 md:w-5 md:h-5" />
              Checklist Preview
            </h3>
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                className="w-full h-[60vh] md:h-[500px] border border-gray-200 dark:border-dark-700 rounded-xl"
                title="PDF Preview"
              />
            ) : (
              <div className="w-full h-[60vh] md:h-[500px] flex items-center justify-center bg-white dark:bg-dark-800 rounded-xl">
                <div className="text-center">
                  <FaSpinner className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading PDF...</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 md:p-6 border-t border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 flex flex-col gap-4">
          <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            By confirming, you agree that the information provided is accurate. Date will be added automatically.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 rounded-xl font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-dark-700 dark:hover:bg-dark-600 transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="flex-[2] flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FaCheck className="w-4 h-4" />
                  Confirm & Submit
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default PDFPreviewModal;
